import { valibotResolver } from "@hookform/resolvers/valibot";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { AppInput } from "~/components/AppInput";
import { Button } from "~/components/ui/button";
import { ResetPasswordForm, resetPasswordSchema } from "~/lib/schemas";
import { resetPasswordServer } from "~/lib/services/user";

type Search = { token?: string };

export const Route = createFileRoute("/reset-password")({
  component: ResetPassword,
  validateSearch: (search: Record<string, unknown>): Search => ({
    token: typeof search.token === "string" ? search.token : undefined,
  }),
});

function ResetPassword() {
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const methods = useForm<ResetPasswordForm>({
    resolver: valibotResolver(resetPasswordSchema),
  });

  if (!token) {
    return (
      <div className="min-h-dvh flex flex-col max-w-2xl mx-auto p-6 justify-center">
        <h1 className="text-3xl font-bold">Missing token</h1>
        <p className="mt-2 text-foreground-muted">
          This reset link is missing its token. Request a new password reset
          email from the login screen.
        </p>
        <Link to="/forgot-password" className="block mt-6">
          <Button className="w-full">Request a new link</Button>
        </Link>
      </div>
    );
  }

  const onSubmit = async (data: ResetPasswordForm) => {
    if (data.password !== data.confirmPassword) {
      setError("Passwords don't match");
      return;
    }
    setError(null);
    setIsLoading(true);
    try {
      const res = await resetPasswordServer({
        data: { token, password: data.password },
      });
      if (res?.error) {
        setError(res.error);
        return;
      }
      setDone(true);
      setTimeout(() => navigate({ to: "/login" }), 1500);
    } catch {
      setError("Could not reset your password. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-dvh flex flex-col max-w-2xl mx-auto p-6 justify-center">
        <h1 className="text-3xl font-bold">Password updated</h1>
        <p className="mt-2 text-foreground-muted">
          Redirecting you to the login screen…
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-dvh flex flex-col max-w-2xl mx-auto p-6">
      <div className="mt-12 mb-8">
        <h1 className="text-4xl font-bold text-black">Choose a new password</h1>
        <p className="text-lg mt-2 text-foreground-muted">
          Pick something at least 8 characters long with a letter and a number.
        </p>
      </div>

      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)} className="space-y-6">
          <div className="relative">
            <AppInput
              name="password"
              type={showPassword ? "text" : "password"}
              label="New password"
            />
            <Button
              type="button"
              className="absolute right-2 top-[65%] -translate-y-1/2 text-sm text-foreground shadow-none bg-transparent hover:bg-transparent underline"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? "Hide" : "Show"}
            </Button>
          </div>
          <AppInput
            name="confirmPassword"
            type={showPassword ? "text" : "password"}
            label="Confirm new password"
          />
          <Button isLoading={isLoading} type="submit" className="w-full">
            Update password
          </Button>
        </form>
      </FormProvider>

      {error && (
        <p className="text-destructive text-sm text-center py-4">{error}</p>
      )}
    </div>
  );
}
